/* INCÓGNITA — Announcement marquee
 *
 * CSS-only marquees (2× duplicated content + translate -50%) glitch when:
 *   - the content width changes after fonts load
 *   - the number of messages is small (long duration makes speed feel uneven)
 *   - the browser rounds the -50% translate to fractional pixels at the seam
 *
 * This ticker measures the actual group width and runs a requestAnimationFrame
 * loop at a fixed pixels-per-second speed, so the seam is perfectly seamless
 * regardless of content size.
 */
(function () {
  'use strict';

  if (customElements.get('marquee-bar')) return;

  class MarqueeBar extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('.header-message__track');
      if (!track) return;

      const originalGroups = track.querySelectorAll('.header-message__group');
      if (originalGroups.length < 1) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) return;

      const speed = parseFloat(this.dataset.speed) || 60; // px/sec
      let groupWidth = 0;
      let position = 0;
      let lastTime = null;
      let paused = false;
      let raf = 0;

      // Keep a reference to the "master" group (first one, from HTML).
      // We duplicate it on demand so the track is always wider than 2× the
      // viewport — that way the loop-wrap never shows a gap on the right.
      const masterGroup = originalGroups[0];

      const ensureCoverage = () => {
        const gw = masterGroup.getBoundingClientRect().width;
        if (gw === 0) return;
        const viewport = this.getBoundingClientRect().width;
        // Need enough groups so that when we've scrolled one group width,
        // the remaining groups still cover the viewport.
        const needed = Math.max(2, Math.ceil((viewport + gw) / gw) + 1);
        while (track.children.length < needed) {
          track.appendChild(masterGroup.cloneNode(true));
        }
      };

      const measure = () => {
        ensureCoverage();
        groupWidth = masterGroup.getBoundingClientRect().width;
      };

      const apply = () => {
        track.style.transform = `translate3d(${-Math.round(position)}px, 0, 0)`;
      };

      const step = (t) => {
        if (lastTime === null) lastTime = t;
        const dt = Math.min((t - lastTime) / 1000, 0.05);
        lastTime = t;
        if (!paused && groupWidth > 0) {
          position += speed * dt;
          if (position >= groupWidth) position -= groupWidth;
          apply();
        }
        raf = requestAnimationFrame(step);
      };

      const start = () => {
        measure();
        track.style.willChange = 'transform';
        track.style.transform = 'translate3d(0, 0, 0)';
        raf = requestAnimationFrame(step);
      };

      start();

      // Recompute after fonts load so a width change doesn't break the seam
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          const prev = groupWidth;
          measure();
          if (Math.abs(prev - groupWidth) > 1) {
            // normalise current position to the new width
            position = position * (groupWidth / prev);
          }
        });
      }

      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(() => {
          const prev = groupWidth;
          measure();
          if (prev > 0 && groupWidth > 0) {
            position = position * (groupWidth / prev);
          }
        });
        ro.observe(masterGroup);
        ro.observe(this);
      } else {
        window.addEventListener('resize', measure);
      }

      // Pause on hover (desktop only)
      this.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') paused = true;
      });
      this.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') paused = false;
      });

      // Pause when the tab is hidden to save CPU
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          paused = true;
        } else {
          paused = false;
          lastTime = null;
        }
      });
    }
  }

  customElements.define('marquee-bar', MarqueeBar);
})();
